import { onUnmounted } from 'vue'

const keyPrefix = 'TIMEOUT_'

/*
A deadline records what it was armed for, not only when it lands. Basket timers
are kept in localStorage so they survive a reload, and every component calling
useBasket re-arms them from there, which means a deadline outlives the basket
that set it. Without the subject, a leftover deadline from a basket the visitor
abandoned would fire against whatever they are doing now - booting them to the
home page part way through a fresh search.
*/
interface Deadline {
  at: number;
  armedFor: string;
}

const setCache = (key: string, deadline: Deadline) =>
  localStorage.setItem(keyPrefix + key, JSON.stringify(deadline))

const getCache = (key: string): Deadline | undefined => {
  const stored = localStorage.getItem(keyPrefix + key)
  if (!stored) return undefined

  try {
    const deadline = JSON.parse(stored)
    if (typeof deadline?.at === 'number' && typeof deadline?.armedFor === 'string') {
      return deadline
    }
  } catch (e) {
    // Unreadable, so there is nothing to check it against.
  }

  // Either corrupt or written before deadlines carried a subject. Dropped
  // rather than fired at whoever the visitor happens to be now.
  return undefined
}

export default function useTimeout (
  key: string,
  callback: () => void,
  currentSubject: () => string
) {
  let timerId: number | undefined

  // The subject can change while the timer is pending - a new basket, or none
  // at all after signing out - and the deadline is no longer about it.
  const fire = (deadline: Deadline) => {
    if (deadline.armedFor !== currentSubject()) return

    callback()
  }

  // A deadline already in the past is dropped rather than fired immediately,
  // which also covers an expiry the API could not give us a number for.
  const arm = (deadline: Deadline) => {
    clearTimeout(timerId)
    const remaining = deadline.at - Date.now()
    if (remaining > 0) timerId = setTimeout(() => fire(deadline), remaining)
  }

  const setTimerFromCache = () => {
    clearTimeout(timerId)
    const deadline = getCache(key)
    if (deadline) arm(deadline)
  }

  const setTimer = (timeout: number, armedFor: string) => {
    const deadline = { at: Date.now() + timeout, armedFor: armedFor }
    arm(deadline)
    setCache(key, deadline)
  }

  const clearTimer = () => {
    clearTimeout(timerId)
    timerId = undefined
    localStorage.removeItem(keyPrefix + key)
  }

  const onStorage = (event: StorageEvent) => {
    if (event.key === keyPrefix + key) setTimerFromCache()
  }

  setTimerFromCache()

  window.addEventListener('storage', onStorage)

  onUnmounted(() => {
    clearTimeout(timerId)
    timerId = undefined
    window.removeEventListener('storage', onStorage)
  })

  return {
    setTimer,
    clearTimer
  }
}
